'use client';

import {
  Box,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from '@mui/material';
import type { ReactNode } from 'react';

export interface Column {
  key: string;
  label: string | ReactNode;
  align?: 'left' | 'center' | 'right';
  width?: string;
}

interface DataTableProps<T> {
  columns: Column[];
  data: T[];
  renderRow: (item: T, index: number) => ReactNode;
  emptyMessage: string;
  getRowKey: (item: T, index: number) => string | number;
  onRowClick?: (item: T) => void;
  maxHeight?: string;
  stickyHeader?: boolean;
  size?: 'small' | 'medium';
  invertHeaderColors?: boolean; // New prop to swap header and body background colors
  // Pagination props
  pagination?: {
    page: number;
    rowsPerPage: number;
    totalElements: number;
    rowsPerPageOptions: number[];
    onPageChange: (page: number) => void;
    onRowsPerPageChange: (rowsPerPage: number) => void;
    labelDisplayedRows?: (info: { from: number; to: number; count: number }) => string;
    labelRowsPerPage?: string;
  };
  // Loading state
  loading?: boolean;
}

// Replace the existing headerCellSx and bodyCellSx constants with these functions
const getHeaderCellSx = (invertHeaderColors?: boolean) => ({
  fontSize: 'body2.fontSize',
  fontWeight: 'bold',

  backgroundColor: invertHeaderColors ? 'grey.100' : 'background.paper',
});

const getBodyCellSx = (invertHeaderColors?: boolean) => ({
  fontSize: 'body2.fontSize',

  backgroundColor: invertHeaderColors ? 'background.paper' : 'grey.100',
});

const headerCellSx = {
  fontSize: 'body2.fontSize',
  fontWeight: 'bold',
};

const bodyCellSx = {
  fontSize: 'body2.fontSize',
  backgroundColor: 'grey.50',
};
// Specific styles for small table
const headerCellSmallSx = {
  fontWeight: 'bold',
  py: 1,
};

const bodyCellSmallSx = {
  fontSize: 'caption.fontSize',
  backgroundColor: 'grey.50',
  py: 0.5,
};

const hoverTableRowSx = {
  '&:hover td': {
    backgroundColor: 'background.paper',
  },
  cursor: 'pointer',
};

const emptyStateCellSx = {
  textAlign: 'center',
};

const loadingContainerSx = {
  display: 'flex',
  justifyContent: 'center',
  p: 5,
};

export default function DataTable<T>({
  columns,
  data,
  renderRow,
  emptyMessage,
  getRowKey,
  onRowClick,
  maxHeight = '40rem',
  stickyHeader = true,
  size = 'medium', // Valor padrão
  pagination,
  loading = false,
  invertHeaderColors = false,
}: DataTableProps<T>) {
  if (loading) {
    return (
      <Box sx={loadingContainerSx}>
        <CircularProgress />
      </Box>
    );
  }

  const getRowSx = () => {
    const baseSx = onRowClick ? hoverTableRowSx : {};
    return baseSx;
  };

  // Seleciona os estilos baseado no tamanho
  // const currentHeaderCellSx = size === "small" ? headerCellSmallSx : headerCellSx

  return (
    <>
      <TableContainer
        sx={{
          ml: 0,
          p: 0,
          width: '100%',
          maxHeight,
        }}
      >
        <Table stickyHeader={stickyHeader} size={size}>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  sx={{
                    ...getHeaderCellSx(invertHeaderColors),
                    ...(column.align && { textAlign: column.align }),
                  }}
                  align={column.align}
                  width={column.width}
                >
                  {column.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {data.map((item, index) => (
              <TableRow
                key={getRowKey(item, index)}
                sx={getRowSx()}
                onClick={onRowClick ? () => onRowClick(item) : undefined}
              >
                {renderRow(item, index)}
              </TableRow>
            ))}
            {data.length === 0 && (
              <TableRow>
                <TableCell sx={emptyStateCellSx} colSpan={columns.length}>
                  <Typography variant="subtitle2">{emptyMessage}</Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {pagination && (
        <TablePagination
          sx={{ width: '100%' }}
          rowsPerPageOptions={pagination.rowsPerPageOptions}
          component="div"
          count={pagination.totalElements}
          rowsPerPage={pagination.rowsPerPage}
          page={pagination.page}
          onPageChange={(e, p) => pagination.onPageChange(p)}
          onRowsPerPageChange={(e) => {
            pagination.onRowsPerPageChange(Number.parseInt(e.target.value, 10));
            pagination.onPageChange(0);
          }}
          labelDisplayedRows={pagination.labelDisplayedRows}
          labelRowsPerPage={pagination.labelRowsPerPage}
        />
      )}
    </>
  );
}

// Export the style functions for use in renderRow functions
export { bodyCellSmallSx, bodyCellSx, getBodyCellSx, getHeaderCellSx, headerCellSmallSx, headerCellSx };
